'use client';

import AuthFormInput from '@c/forms/components/AuthFormInput';
import { AppleBrandIcon, FacebookBrandIcon, GoogleBrandIcon } from '@c/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@ui/Button';
import Checkbox from '@ui/Checkbox';
import { updateUserDoc } from '@util/firestore/users';
import { logError } from '@util/logError';
import AuthProvider, { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { FirebaseError } from 'firebase/app';
import { FunctionsError } from 'firebase/functions';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

type EmailInfoSignupTabProps = {
  redirectError: string | null;
  setTabIndex: (index: number) => void;
};

export default function EmailInfoSignupTab({
  redirectError,
  setTabIndex,
}: EmailInfoSignupTabProps) {
  const [authError, setAuthError] = useState<{
    id: string;
    message: string;
  } | null>(
    redirectError ? { id: 'social_error', message: redirectError } : null
  );
  const { showSuccessToast } = useToastContext();
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  // const [emailLinkSent, setEmailLinkSent] = useState(false);
  // const [sentToEmail, setSentToEmail] = useState('');
  const { user, linkSocialToCredential, sendEmailVerification } = useAuth();

  const formSchema = z.object({
    email: z.string().email('Invalid email address'),
    optOut: z.boolean().optional(),
  });

  type Form = z.infer<typeof formSchema>;

  const { register, handleSubmit, formState, watch, setValue, getValues } =
    useForm<Form>({
      mode: 'onBlur',
      resolver: zodResolver(formSchema),
    });

  const onSocialClick = async (provider: string) => {
    try {
      sessionStorage.setItem('auth:redirecting', Date.now().toString());
      const res = await linkSocialToCredential(provider as AuthProvider);

      if (res instanceof FirebaseError) {
        setAuthError({
          id: 'social_error',
          message: res.message,
        });
        return;
      }

      if (res?.user?.uid) {
        await updateUserDoc(res.user.uid, {
          email: res.user.email ?? '',
          opted_out: getValues('optOut') ?? false,
        });

        showSuccessToast('Account linked successfully');
        setTabIndex(2);
      }
    } catch (error) {
      sessionStorage.removeItem('auth:redirecting');
      if (error instanceof FirebaseError) {
        const capitalizedProvider =
          provider.charAt(0).toUpperCase() + provider.slice(1);

        if (error.message === 'auth/credential-already-in-use') {
          setAuthError({
            id: 'social_error',
            message: `${capitalizedProvider} email already in use.`,
          });
          return;
        }

        if (error.message === 'auth/popup-closed-by-user') {
          setAuthError({
            id: 'social_error',
            message: 'Popup closed by the user. Please try again.',
          });
          return;
        }

        setAuthError({
          id: 'social_error',
          message: error.message,
        });
      } else {
        setAuthError({
          id: 'social_error',
          message: 'An error occurred while linking with social provider',
        });
        logError(error);
      }
    }
  };

  const onSendEmail = async (data: Form) => {
    setIsSendingEmail(true);
    try {
      const { status } = await sendEmailVerification(data.email);
      if (status === 'already_verified')
        showSuccessToast('Email already verified.');
      else showSuccessToast('Email verification sent.');

      if (user?.uid) {
        // not important to wait or fail the flow if this fails
        void updateUserDoc(user.uid, { opted_out: !!data.optOut }).catch(
          (e) => {
            logError(e, 'onSendEmail.updateUserDoc');
          }
        );
      }
      setTabIndex(2);
    } catch (e) {
      logError(e as Error);
      if (
        e instanceof FunctionsError &&
        e.code === 'functions/already-exists'
      ) {
        setAuthError({
          id: 'email_in_use_error_' + Date.now(),
          message: 'This email address is already in use by another account.',
        });
      } else if (
        e instanceof FunctionsError &&
        e.code === 'functions/invalid-argument'
      ) {
        setAuthError({
          id: 'invalid_email_error_' + Date.now(),
          message: 'Invalid email address.',
        });
      } else if (
        e instanceof FunctionsError &&
        e.code === 'functions/resource-exhausted'
      ) {
        setAuthError({
          id: 'resource_exhausted_error_' + Date.now(),
          message:
            'A verification email was already sent. Check your inbox or wait a few minutes before requesting another.',
        });
      } else {
        setAuthError({
          id: 'unknown_error_' + Date.now(),
          message:
            'An error occurred. Please try a different method or contact support.',
        });
      }
    } finally {
      setIsSendingEmail(false);
    }
  };

  // if (emailLinkSent) {
  //   return (
  //     <motion.div
  //       initial={{ opacity: 0, y: 20 }}
  //       animate={{ opacity: 1, y: 0 }}
  //       transition={{ duration: 0.5 }}
  //       className="w-full sm:max-w-[50rem]"
  //     >
  //       <motion.h1
  //         initial={{ opacity: 0 }}
  //         animate={{ opacity: 1 }}
  //         transition={{ delay: 0.2 }}
  //         className="text-[3rem] font-semibold"
  //       >
  //         Check your email
  //       </motion.h1>
  //       <motion.p
  //         initial={{ opacity: 0 }}
  //         animate={{ opacity: 1 }}
  //         transition={{ delay: 0.3 }}
  //         className="mt-[0.8rem] text-[1.8rem] text-[#444444]"
  //       >
  //         An account already exists with{' '}
  //         <span className="font-semibold">{sentToEmail}</span>. We sent a
  //         sign-in link to your email. Click the link to sign into your existing
  //         account and link your phone number.
  //       </motion.p>
  //     </motion.div>
  //   );
  // }

  return (
    <form onSubmit={handleSubmit(onSendEmail)}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full sm:max-w-[50rem]"
      >
        <motion.h1
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-[3rem] font-semibold"
        >
          What&apos;s your email?
        </motion.h1>
        <motion.h2
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-[0.4rem] text-[1.8rem] text-[#444444]"
        >
          Used for sign-in, account updates, and optional marketing emails.
        </motion.h2>

        {authError && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-8 flex flex-col"
          >
            <span className="font-bold tracking-widest text-brand-secondary">
              ERROR
            </span>

            <p
              role="alert"
              id={authError.id}
              className="text-[1.8rem text-brand-secondary"
            >
              {authError.message}
            </p>
          </motion.div>
        )}

        <motion.h2
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-16 text-[1.8rem] text-[#444444]"
        >
          <AuthFormInput
            label="Email"
            name="email"
            required
            type="email"
            placeholder="Your best email"
            register={register('email')}
            errorMessage={formState.errors.email?.message}
            errorId={
              formState.errors.email?.type
                ? `email_error_${formState.errors.email.type}_${Date.now()}`
                : authError?.id
            }
          />
        </motion.h2>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-16 flex w-full text-[1.8rem]"
        >
          <Checkbox
            label="I do not wish to receive marketing communications about MX Locker products & services."
            selected={watch('optOut')}
            onChange={() => {
              setValue('optOut', !watch('optOut'));
              sessionStorage.setItem(
                'optOut',
                !watch('optOut') ? 'true' : 'false'
              );
            }}
          />
        </motion.div>

        <motion.h2
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-16 text-[1.8rem] text-[#444444]"
        >
          <Button
            text={'Continue'}
            type="secondary"
            loading={isSendingEmail}
            buttonType="submit"
            className="!h-[5.5rem] w-full !text-[1.8rem]"
          />
        </motion.h2>

        <motion.div
          className="mt-8 flex h-[2.4rem] justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <p className="text-[1.8rem] text-[#444444]">
            or bring your email from
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8  flex gap-[0.4rem]"
        >
          <Button
            text={''}
            leadingIcon={<FacebookBrandIcon />}
            type="sso"
            onClick={() => onSocialClick('facebook')}
            width="fluid"
            ariaText="Login with facebook"
          />
          <Button
            text={''}
            leadingIcon={<GoogleBrandIcon />}
            type="sso"
            onClick={() => onSocialClick('google')}
            width="fluid"
            ariaText="Login with google"
          />
          <Button
            text={''}
            leadingIcon={<AppleBrandIcon />}
            type="sso"
            onClick={() => onSocialClick('apple')}
            width="fluid"
            ariaText="Login with apple"
          />
        </motion.div>
      </motion.div>
    </form>
  );
}
